import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { useKeyedState } from "@/hooks/useKeyedState";
import { useIsCompactViewport } from "@/hooks/use-device";

// The legacy key was force-written to `true` by any narrow-viewport visit,
// so its stored values don't reflect a real user choice. A fresh key starts
// everyone from the auto-open default; only explicit wide-screen collapses
// persist under it.
const LEGACY_COLLAPSED_KEY = "assistantSidebarCollapsed";
/** Exported so the loading skeleton can honor a persisted collapse. */
export const SESSION_SIDEBAR_COLLAPSED_KEY = "codesmithSidebarCollapsed";
type SetCollapsed = (value: boolean | ((prev: boolean) => boolean)) => void;

/**
 * Collapsed state for the session sidebar that only auto-opens when the
 * container has room for it (see `usePanelSpaceBudget`). While space exists
 * the stored preference is the source of truth; without space the sidebar
 * starts collapsed regardless, and a manual open is tracked separately so a
 * cramped window never rewrites the wide-screen preference.
 *
 * `arrivedInProofFocus`: the shell mounted on a Proof focus link (GitHub's
 * Proof links, see `proofFocusFromQuery`). The sidebar starts collapsed for
 * it, past the end of focus mode, until the user opens it, and the
 * preference is never written. Entering focus from inside the app (the
 * palette's Proof match) leaves the sidebar as it is.
 *
 * `glides` is false from the moment the container gains or loses room for the
 * list until the user next toggles or peeks it (`markUserDriven`), and always
 * false at phone width: a resize was not the user's toggle, so the list snaps.
 */
export function useSidebarCollapsedState(containerHasSpace, arrivedInProofFocus) {
  const $ = _c(14);
  const isCompactViewport = useIsCompactViewport();
  const hasSpaceForSidebar = containerHasSpace && !isCompactViewport;
  const [storedCollapsed, setStoredCollapsed] = useLocalStorage(SESSION_SIDEBAR_COLLAPSED_KEY, false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
  const [openedWhileNarrow, setOpenedWhileNarrow] = useKeyedState(hasSpaceForSidebar, false);
  const [userDriven, setUserDriven] = useKeyedState(hasSpaceForSidebar, false);
  const [proofFocusHold, setProofFocusHold] = useState(arrivedInProofFocus);
  const isSidebarCollapsed = proofFocusHold || (hasSpaceForSidebar ? storedCollapsed : !openedWhileNarrow);
  let t1;
  if ($[1] !== hasSpaceForSidebar || $[2] !== proofFocusHold || $[3] !== setOpenedWhileNarrow || $[4] !== setStoredCollapsed || $[5] !== setUserDriven) {
    t1 = value => {
      setUserDriven(true);
      if (proofFocusHold) {
        const nextCollapsed = typeof value === "function" ? value(true) : value;
        if (nextCollapsed) {
          return;
        }
        setProofFocusHold(false);
      }
      const resolved = proofFocusHold ? false : value;
      if (hasSpaceForSidebar) {
        setStoredCollapsed(resolved);
        return;
      }
      setOpenedWhileNarrow(prevOpened => {
        const nextCollapsed_0 = typeof resolved === "function" ? resolved(!prevOpened) : resolved;
        return !nextCollapsed_0;
      });
    };
    $[1] = hasSpaceForSidebar;
    $[2] = proofFocusHold;
    $[3] = setOpenedWhileNarrow;
    $[4] = setStoredCollapsed;
    $[5] = setUserDriven;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const setIsSidebarCollapsed = t1;
  let t2;
  if ($[7] !== setUserDriven) {
    t2 = () => setUserDriven(true);
    $[7] = setUserDriven;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const markUserDriven = t2;
  const t3 = userDriven && !isCompactViewport;
  let t4;
  if ($[9] !== isSidebarCollapsed || $[10] !== markUserDriven || $[11] !== setIsSidebarCollapsed || $[12] !== t3) {
    t4 = [isSidebarCollapsed, setIsSidebarCollapsed, t3, markUserDriven];
    $[9] = isSidebarCollapsed;
    $[10] = markUserDriven;
    $[11] = setIsSidebarCollapsed;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function _temp() {
  window.localStorage.removeItem(LEGACY_COLLAPSED_KEY);
}